import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import NotificationsService from "../services/api/notifications/notifications.service";
import { NotificationsTab, RefreshReactQuery } from "../constants/AppConstants";
import { useNotification } from "./notification.hook";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

const NOTIFICATIONS_COUNTERS_QUERY_KEY = "get-notifications-counters";
const NOTIFICATIONS_LIST_QUERY_KEY = "get-notifications";

export const useNotificationsCounters = (showPanel: boolean) => {
  const { notification } = useNotification();
  const {
    data: counters,
    refetch: refetchCounters,
    isError,
  } = useQuery({
    queryKey: [NOTIFICATIONS_COUNTERS_QUERY_KEY],
    queryFn: () => NotificationsService.getCounters(),
    staleTime: RefreshReactQuery.MODERATE,
    refetchInterval: showPanel ? false : 60000,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { counters, refetchCounters };
};

export const useReadAllNotifications = () => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => NotificationsService.readAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_COUNTERS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_QUERY_KEY],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });
  return { mutateAsync, isPending };
};

export const useReadSingleNotification = () => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync } = useMutation({
    mutationFn: (id: number) => NotificationsService.read(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_COUNTERS_QUERY_KEY],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });
  return { mutateAsync };
};

interface UseNotificationsListParams {
  enabled: boolean;
  categoryValue: string;
  selectedTabValue: NotificationsTab;
  pageSize: number;
}

export const useNotificationsList = ({
  enabled,
  categoryValue,
  selectedTabValue,
  pageSize,
}: UseNotificationsListParams) => {
  const { notification } = useNotification();
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearch] = useDebounce(searchValue, 500);

  const queryParams = useMemo(
    () => [
      ...(categoryValue !== "ALL"
        ? [{ key: "category", value: [categoryValue] }]
        : []),
      ...(selectedTabValue !== NotificationsTab.ALL
        ? [
            {
              key: "read",
              value: [selectedTabValue === NotificationsTab.READ],
            },
          ]
        : []),
    ],
    [categoryValue, selectedTabValue],
  );

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: [NOTIFICATIONS_LIST_QUERY_KEY, debouncedSearch, queryParams],
      queryFn: ({ pageParam, signal }) =>
        NotificationsService.list(
          pageSize,
          pageParam,
          [
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
            ...queryParams,
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * pageSize < lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
      enabled,
    });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const initLoading = isLoading;
  const fetchingMore = isFetching && (data?.pages.length ?? 0) > 0;

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    notifications,
    initLoading,
    fetchingMore,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    searchValue,
    setSearchValue,
  };
};
